/**
 * Custom hook providing connection operations for the knowledge graph chat.
 * Operates on Apollo reactive variables for state management.
 * @remarks
 * Provides operations for managing SSE connection state, error handling,
 * and connection reset flags. Follows immutable patterns and uses useCallback
 * for stable function references.
 *
 * Operations include:
 * - Updating connection status (disconnected, connecting, connected, etc.)
 * - Aborting the answer stream currently in flight
 * - Handling connection errors
 * - Clearing error state
 *
 * All operations are wrapped in useCallback for stable references across renders.
 * @returns Connection operation functions for chat state management
 */

import { useCallback } from "react";

import { closeActiveStream } from "../utils/activeStream";

import type { ConnectionStatus } from "@/stores/knowledgeGraph";
import { connectionStatusVar, errorVar } from "@/stores/knowledgeGraph";

/**
 * Hook providing connection operations for knowledge graph chat.
 * @returns Object containing connection operation functions
 */
export const useConnectionOperations = () => {
  /**
   * Update the current connection status.
   * @param status - New connection status
   */
  const setConnectionStatus = useCallback((status: ConnectionStatus) => {
    connectionStatusVar(status);
  }, []);

  /**
   * Abort the answer stream currently in flight, if any.
   * @remarks SE-6900: replaces `setShouldResetConnection`, which raised a
   * reactive flag no consumer ever read — so the teardown it was named for
   * never happened and an abandoned stream kept writing into the conversation.
   * @returns True when a stream was actually torn down
   */
  const resetConnection = useCallback((): boolean => {
    const closed = closeActiveStream();
    if (closed) {
      connectionStatusVar("disconnected");
    }
    return closed;
  }, []);

  /**
   * Handle connection error by setting error message and status.
   * @param error - Error message
   */
  const handleConnectionError = useCallback((error: string) => {
    errorVar(error);
    connectionStatusVar("error");
  }, []);

  /**
   * Clear the current error state.
   */
  const clearError = useCallback(() => {
    errorVar(null);
  }, []);

  return {
    setConnectionStatus,
    resetConnection,
    handleConnectionError,
    clearError,
  };
};
